import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RotateCcw } from "lucide-react";
import { drawArchetype } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

const ease = [0.22, 1, 0.36, 1];

export const Oracle = () => {
  const [intention, setIntention] = useState("");
  const [loading, setLoading] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [result, setResult] = useState(null);

  const draw = async () => {
    setLoading(true);
    setFlipped(false);
    try {
      const data = await drawArchetype({ intention: intention || null });
      setResult(data);
      setTimeout(() => setFlipped(true), 250);
    } catch (e) {
      toast.error("The oracle is silent. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFlipped(false);
    setTimeout(() => setResult(null), 700);
  };

  return (
    <section id="oracle" className="relative w-full py-28 md:py-40" data-testid="oracle-section">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[50vh] w-[50vh] -translate-x-1/2 rounded-full bg-[#3B2A48]/20 blur-[130px]" />

      <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease }}
        >
          <p className="overline mb-6">The Interactive Oracle</p>
          <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] font-light leading-[1.02] tracking-tight text-white">
            Draw an <span className="italic text-[#C5A059]">Archetype</span>.
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/55">
            Name your intention, and the oracle will reveal the matriarchal
            archetype moving through you — with a statement of power spoken
            just for this moment.
          </p>

          <div className="mt-10 max-w-md">
            <input
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              placeholder="Your intention (optional)…"
              maxLength={120}
              className="w-full rounded-full border border-[#C5A059]/25 bg-white/5 px-6 py-4 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#C5A059]/70"
              data-testid="oracle-intention-input"
            />
            <div className="mt-5 flex items-center gap-4">
              <button
                onClick={draw}
                disabled={loading}
                className="cta-glow group inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-8 py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#050505] transition-transform duration-300 hover:-translate-y-1 disabled:opacity-60"
                data-testid="oracle-draw-btn"
              >
                <Sparkles size={16} strokeWidth={1.5} />
                {loading ? "Consulting…" : result ? "Draw again" : "Draw an archetype"}
              </button>
              {result && (
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-[#C5A059]"
                  data-testid="oracle-reset-btn"
                >
                  <RotateCcw size={14} strokeWidth={1.5} /> Reset
                </button>
              )}
            </div>
            {result && (
              <div className="mt-5">
                <ShareButton result={result} />
              </div>
            )}
          </div>
        </motion.div>

        {/* Flip card */}
        <div className="flex items-center justify-center">
          <div className="perspective-1200 h-[520px] w-[340px]">
            <motion.div
              className="preserve-3d relative h-full w-full"
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 1, ease }}
            >
              {/* Front — card back */}
              <div className="backface-hidden absolute inset-0 flex flex-col items-center justify-center rounded-3xl border border-[#C5A059]/30 bg-[#0a0a0a] p-8 shadow-[0_0_60px_rgba(59,42,72,0.5)]">
                <div className="flex h-full w-full flex-col items-center justify-center rounded-2xl border border-[#C5A059]/15">
                  <div className="relative flex h-28 w-28 items-center justify-center">
                    <div className="absolute inset-0 rounded-full border border-[#C5A059]/40" />
                    <div className="absolute inset-3 rounded-full border border-[#C5A059]/25" />
                    <Sparkles className="text-[#C5A059]" size={34} strokeWidth={1} />
                  </div>
                  <p className="mt-8 font-serif text-3xl font-light italic tracking-wide text-[#C5A059]">
                    Sybils
                  </p>
                  <p className="overline mt-3 text-[0.6rem]">The Oracle Deck</p>
                </div>
              </div>

              {/* Back — reveal */}
              <div
                className="backface-hidden absolute inset-0 overflow-hidden rounded-3xl border border-[#C5A059]/40 bg-[#0a0a0a] shadow-[0_0_70px_rgba(59,42,72,0.7)]"
                style={{ transform: "rotateY(180deg)" }}
                data-testid="oracle-result-card"
              >
                {result && (
                  <div className="flex h-full flex-col">
                    <div className="relative h-1/2 overflow-hidden">
                      <img
                        src={result.image}
                        alt={result.name}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                      <span className="absolute right-4 top-4 rounded-full border border-[#C5A059]/40 px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-[#C5A059]">
                        {result.element}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-serif text-3xl font-light text-[#C5A059]">
                        {result.name}
                      </h3>
                      <p className="overline mt-1 text-[0.58rem]">{result.title}</p>
                      <p className="mt-4 flex-1 overflow-y-auto text-sm font-light italic leading-relaxed text-white/70">
                        “{result.statement}”
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Oracle;
